// Team voice wire format (docs/API.md section 9, open mic).
// Client -> server binary: raw PCM s16le mono 16 kHz, 320 samples per frame.
// Server -> client binary: 17-byte header, then the same PCM.
//   byte 0: 2 = voice (any other value is ignored; 1 was the old radio)
//   bytes 1..16: the speaker's user id (UUID, 16 raw bytes)

export const SAMPLE_RATE = 16_000;
export const FRAME_SAMPLES = 320; // 20 ms
export const HEADER_BYTES = 17;

/** The only kind byte a client plays. */
export const KIND_VOICE = 2;

export interface VoiceFrame {
  userId: string;
  pcm: Int16Array;
}

const HEX = /^[0-9a-f]{32}$/;

export function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, "").toLowerCase();
  if (!HEX.test(hex)) throw new Error(`not a UUID: ${uuid}`);
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function bytesToUuid(b: Uint8Array): string {
  let hex = "";
  for (let i = 0; i < 16; i++) hex += (b[i] ?? 0).toString(16).padStart(2, "0");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Server-format frame (used by tests and as the reference for the header). */
export function buildFrame(userId: string, pcm: Int16Array, kind = KIND_VOICE): ArrayBuffer {
  const buf = new ArrayBuffer(HEADER_BYTES + pcm.length * 2);
  const u8 = new Uint8Array(buf);
  u8[0] = kind;
  u8.set(uuidToBytes(userId), 1);
  const view = new DataView(buf);
  for (let i = 0; i < pcm.length; i++) view.setInt16(HEADER_BYTES + i * 2, pcm[i] ?? 0, true);
  return buf;
}

/** Parse a server frame. Returns null for any other kind byte and anything malformed (odd or empty payload). */
export function parseFrame(buf: ArrayBuffer): VoiceFrame | null {
  if (buf.byteLength <= HEADER_BYTES || (buf.byteLength - HEADER_BYTES) % 2 !== 0) return null;
  const u8 = new Uint8Array(buf);
  if (u8[0] !== KIND_VOICE) return null;
  const userId = bytesToUuid(u8.subarray(1, HEADER_BYTES));
  const n = (buf.byteLength - HEADER_BYTES) / 2;
  const pcm = new Int16Array(n);
  const view = new DataView(buf);
  for (let i = 0; i < n; i++) pcm[i] = view.getInt16(HEADER_BYTES + i * 2, true);
  return { userId, pcm };
}

/** Client-format frame: just the PCM, little-endian regardless of platform. */
export function encodePcm(pcm: Int16Array): ArrayBuffer {
  const buf = new ArrayBuffer(pcm.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < pcm.length; i++) view.setInt16(i * 2, pcm[i] ?? 0, true);
  return buf;
}
